<template>
  <context-bar-and-content class="early-bird-application" :display-footer="applicationStarted && !ebApplicationComplete">
    <template slot="context-bar-left">
      <strong v-if="!applicationStarted">EARLY BIRD APPLICATION</strong>
      <el-button icon="el-icon-arrow-left" @click="exitApplication" v-else>Exit Application</el-button>
    </template>
    <template slot="context-bar-middle" v-if="applicationStarted">
      {{ this.client.firstName && this.client.lastName ? this.client.lastName + ', ' + this.client.firstName : 'New Application' }}
    </template>
    <template slot="context-bar-right" v-if="applicationStarted && !this.ebApplicationComplete">
      <el-button type="primary" @click="saveChanges" v-if="!(isLastStep || currentStepName === 'eb-qualifications')">Save</el-button>
    </template>
    <el-row class="find-client" v-if="!applicationStarted" justify="center" :gutter="0" v-loading="isLoading">
      <el-col :span="24">
        <h2 class="align-center">Begin or Continue an Early Bird Application</h2>
      </el-col>
      <el-col class="ssn-or-upload-wrapper" :lg="{ span: 16, offset: 4 }">
        <el-col :span="12" class="pa-3">
          <h2>Enter the Primary Taxpayer's SSN </h2>
          <lookup-client-by-social @startNewApplication="startApplicationFromSSNLookup" @continueApplication="continueApplicationForClientId"></lookup-client-by-social>
        </el-col>
        <el-col :span="12" class="pa-3">
          <h2>Import Client Data</h2>
          <import-client-data @success="onSuccessfulImport"></import-client-data>
        </el-col>
      </el-col>
    </el-row>
    <template v-if="applicationStarted">
      <template v-if="!ebApplicationComplete">
        <div class="steps">
          <el-steps :active="currentStep" align-center finish-status="success">
            <el-step title="Client Details"></el-step>
            <el-step title="Add Paystubs"></el-step>
            <el-step title="Print W-2s"></el-step>
            <el-step title="Estimate Tax Refund"></el-step>
            <el-step title="Qualifications"></el-step>
            <el-step title="Enroll Client"></el-step>
          </el-steps>
        </div>
        <div class="step-content" v-loading="isLoading">
          <el-alert class="mb-3" :title="errorMessage" type="error" show-icon v-if="errorMessage"></el-alert>
          <keep-alive>
            <component :is="componentForCurrentStep" ref="currentStepComponent" @nextStep="nextStep"></component>
          </keep-alive>
        </div>
      </template>
      <template v-else>
        <div class="step-content">
          <enrollment-complete-card></enrollment-complete-card>
        </div>
      </template>
    </template>
    <template slot="footer-left">
      <el-button icon="el-icon-arrow-left" @click="previousStep" :disabled="step == 0" v-if="!isLastStep">Previous</el-button>
    </template>
    <template slot="footer-middle">
      &nbsp;
    </template>
    <template slot="footer-right">
      <el-button type="primary" @click="nextStep" v-if="!isLastStep && currentStepName !== 'eb-qualifications'">
        Next<i class="el-icon-arrow-right el-icon-right"></i>
      </el-button>
    </template>
  </context-bar-and-content>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import get from 'lodash/get';
import ContextBarAndContent from '@/components/layout/ContextBarAndContent.vue';
import LookupClientBySocial from '@/components/earlyBird/LookupClientBySocial.vue';
import EarlyBirdClientDetails from '@/components/earlyBird/ClientDetails.vue';
import EarlyBirdPaystub from '@/components/earlyBird/Paystub.vue';
import EarlyBirdPrintW2 from '@/components/earlyBird/PrintW2.vue';
import EnrollmentCompleteCard from '@/components/earlyBird/EnrollmentCompleteCard.vue';
import EarlyBirdTaxReturnEstimate from '@/components/earlyBird/TaxReturnEstimate.vue';
import EarlyBirdQualifications from '@/components/earlyBird/Qualifications.vue';
import EarlyBirdEnroll from '@/components/earlyBird/Enroll.vue';
import ImportClientData from '@/components/form-item/ImportClientData.vue';
import { STATE } from '@/enum/EarlyBirdApplicationStates';
import { Client } from '@/model';


export default {
  name: 'eb-application',
  data() {
    return {
      isLoading: false,
      applicationStarted: false,
      step: 0,
      errorMessage: null,
      steps: [
        EarlyBirdClientDetails,
        EarlyBirdPaystub,
        EarlyBirdPrintW2,
        EarlyBirdTaxReturnEstimate,
        EarlyBirdQualifications,
        EarlyBirdEnroll,
      ],
    };
  },
  watch: {
    '$route.params.clientId': {
      immediate: true,
      handler(clientId) {
        if (clientId !== undefined) {
          this.continueApplicationForClientId(clientId);
        }
      },
    },
    client: function updateRouteWhenClientIdPopulated(client) {
      if (client.id !== undefined && this.$route.params.clientId === undefined) {
        this.$router.replace({
          ...this.$router.currentRoute,
          params: {
            clientId: client.id,
          },
        });
      }
    },
  },
  methods: {
    ...mapActions('Client', [
      'loadClient',
      'updateClient',
      'setClient',
      'resetState',
      'loadClientEarlyBird',
    ]),
    async startApplicationFromSSNLookup(ssn) {
      this.resetState();
      await this.setClient(new Client({
        ssn,
      }));

      this.startApplication();
    },
    async continueApplicationForClientId(clientId) {
      this.resetState();
      this.isLoading = true;
      try {
        await Promise.all([
          this.loadClient(clientId),
          this.loadClientEarlyBird(clientId),
        ]);

        this.startApplication();
      } catch (e) {
        console.warn(e);
        this.$notify({
          type: 'error',
          title: 'Error',
          message: 'An error occurred while initializing the application',
        });
      } finally {
        this.isLoading = false;
      }
    },
    nextStep(stepCount) {
      this.errorMessage = null;
      const currentStep = this.$refs.currentStepComponent;

      if (currentStep.$options.name === 'eb-client-details' && get(this, 'ebQualification.refundAmt', undefined) !== undefined) {
        // launch all the way to qualifications
        stepCount = 4;
      }

      if (currentStep.save) {
        this.isLoading = true;
        currentStep.save().then(() => {
          if (stepCount > 1) {
            this.step += stepCount;
          } else {
            this.step++;
          }
        }).catch((e) => {
          this.errorMessage = get(e, 'response.data', 'An error occurred. Please try again.');
        }).finally(() => {
          this.isLoading = false;
        });
      } else if (stepCount > 1) {
        this.step += stepCount;
      } else {
        this.step++;
      }
    },
    previousStep() {
      this.step--;
    },
    exitApplication() {
      this.applicationStarted = false;
      this.step = 0;
      this.resetState();
      this.$router.replace({
        ...this.$router.currentRoute,
        params: {
          clientId: undefined,
        },
      });
    },
    saveChanges() {
      this.errorMessage = null;
      if (this.$refs.currentStepComponent && this.$refs.currentStepComponent.save) {
        this.isLoading = true;
        this.$refs.currentStepComponent.save().then(() => {
          this.$notify.success({
            title: 'Success',
            message: 'Application saved.',
            position: 'bottom-right',
          });
        }).catch((e) => {
          this.errorMessage = get(e, 'response.data', 'An error occurred. Please try again.');
        }).finally(() => {
          this.isLoading = false;
        });
      }
    },
    onSuccessfulImport(client) {
      this.setClient(client).then(() => {
        this.startApplication();
      });
    },
    startApplication() {
      this.errorMessage = null;
      if (!this.ebApplicationComplete) {
        switch (this.ebApplicationState) {
          case STATE.QUALIFIED:
            this.step = 5;
            break;
          default:
            break;
        }
      }
      this.applicationStarted = true;
    },
  },
  computed: {
    ...mapGetters('Client', [
      'client',
      'ebApplicationComplete',
      'ebApplicationState',
      'ebQualification',
    ]),
    currentStep() {
      return this.step % this.totalSteps;
    },
    componentForCurrentStep() {
      return this.steps[this.step];
    },
    currentStepName() {
      return get(this, 'componentForCurrentStep.name', null);
    },
    totalSteps() {
      return this.steps.length;
    },
    isLastStep() {
      return this.totalSteps - this.step <= 1;
    },
  },
  mounted() {
    if (this.$route.params && this.$route.params.useCurrent) {
      this.startApplication();
    }
  },
  components: {
    ContextBarAndContent,
    LookupClientBySocial,
    EarlyBirdClientDetails,
    EarlyBirdPaystub,
    EarlyBirdPrintW2,
    EarlyBirdTaxReturnEstimate,
    EarlyBirdQualifications,
    EarlyBirdEnroll,
    ImportClientData,
    EnrollmentCompleteCard,
  },
};

</script>

<style lang="scss">

.early-bird-application {
  .ssn-or-upload-wrapper {
    background: #F2F2F2;
    border-radius: 20px;

    > .el-col:not(:first-child) {
      border-left: 1px solid #CCC;
    }
  }

  .steps, .step-content {
    width: calc(100% - 36px);
    padding: 18px;
  }
}

</style>
